import type { Router, RouteRecordRaw, RouterScrollBehavior } from 'vue-router';

import { scrollToHash } from '#/lib/scrollToHash';
import { useAuthStore } from '#/stores/auth';

/**
 * FO 라우터 정의 — routes·scrollBehavior·인증 가드를 export 한다(Router 인스턴스는 생성하지 않음).
 *
 * vite-ssg(SSG) 전환: ViteSSG 가 routes 로 Router 를 생성한다(빌드=memoryHistory, 브라우저=webHistory).
 * 따라서 이 모듈은 createRouter 를 호출하지 않고 옵션·가드만 제공하며, main.ts 가 ViteSSG 에 넘긴다.
 *
 * 레이아웃: meta.layout = 'workspace'(2차 AI 분석 셸) | 'blank'(셸 없음). App.vue 리졸버가 선택.
 * 인증: meta.requiresAuth 라우트는 registerAuthGuard 가 보호(미로그인 → /error/auth-required).
 * 제목: meta.title 은 모든 화면 라우트에 필수(useRouteHead 가 `<title>` 조립 — types/router.d.ts 참조).
 *
 * AI 입시분석 워크스페이스 단독 서비스(int-jnd-ipsi-ai-platform) — 커머스(자료 탐색·장바구니·결제·자료실·
 * 구매내역·1:1 문의·후기·공지·가이드·이벤트)·구글 SSO 는 전부 제거됐다. `/` 가 곧 워크스페이스 랜딩이며,
 * 구 `/analysis` 경로 세그먼트는 제거돼 하위 화면이 전부 루트 직속으로 승격됐다.
 */

export const routes: RouteRecordRaw[] = [
  // 홈 — 워크스페이스 단독 서비스라 `/` 가 곧 랜딩이다(리다이렉트 경유 없음).
  {
    path: '/',
    name: 'analysis-home',
    component: () => import('#/pages/fo/analysis/AnalysisHomePage.vue'),
    meta: { layout: 'workspace', title: 'AI 입시분석 워크스페이스' },
  },
  {
    path: '/legal/terms',
    name: 'legal-terms',
    component: () => import('#/pages/fo/legal/TermsPage.vue'),
    meta: { layout: 'workspace' },
  },
  {
    path: '/legal/privacy',
    name: 'legal-privacy',
    component: () => import('#/pages/fo/legal/PrivacyPage.vue'),
    meta: { layout: 'workspace' },
  },
  {
    path: '/legal/business',
    name: 'legal-business',
    component: () => import('#/pages/fo/legal/BusinessPage.vue'),
    meta: { layout: 'workspace' },
  },
  // ── Blank 레이아웃(셸 없음): 인증·예외 ─────────────────────────
  {
    path: '/login',
    name: 'login',
    component: () => import('#/pages/fo/LoginPage.vue'),
    meta: { layout: 'blank', title: '로그인' },
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('#/pages/fo/SignupPage.vue'),
    meta: { layout: 'blank', title: '회원가입' },
  },
  {
    path: '/error/auth-required',
    name: 'error-auth-required',
    component: () => import('#/pages/fo/error/AuthRequiredPage.vue'),
    meta: { layout: 'blank', title: '로그인이 필요합니다' },
  },
  // ── 2차 AI 입시분석 화면 — 워크스페이스 셸(layout='workspace') ────────
  // 내 입시카드(035 WS-01) — 모든 분석의 공통 입력 자산. 본인 성적이라 인증 필요.
  {
    path: '/card',
    name: 'analysis-card',
    component: () => import('#/pages/fo/analysis/IpsiCardPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '내 입시카드' },
  },
  // 정시 분석(036 WS-02). 077 — 비로그인은 샘플 뷰, 회원 데이터 분기는 화면(페이지 스위치)이 세션으로
  // 소유한다. 라우트 가드를 두지 않아도 회원 데이터는 세션이 있어야만 조회된다(API 인증 무변경).
  {
    path: '/jeongsi',
    name: 'analysis-jeongsi',
    component: () => import('#/pages/fo/analysis/JeongsiAnalysisPage.vue'),
    meta: { layout: 'workspace', title: '정시 분석' },
  },
  // 수시 분석(040 WS-03). 077 — 비로그인은 샘플 뷰, 회원 데이터 분기는 화면이 세션으로 소유(정시와 동형).
  {
    path: '/susi',
    name: 'analysis-susi',
    component: () => import('#/pages/fo/analysis/SusiAnalysisPage.vue'),
    meta: { layout: 'workspace', title: '수시 분석' },
  },
  // 수시 6장 카드 리포트(060) — 조건 입력·사전 카운트·생성(서버 규칙 조합).
  {
    path: '/susi/cards',
    name: 'analysis-susi-cards',
    component: () => import('#/pages/fo/analysis/SusiCardsPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '수시 6장 카드' },
  },
  // 리포트 재열람 — 저장 스냅샷 재현(본인 것만, 서버 404 격리).
  {
    path: '/susi/cards/:reportId(\\d+)',
    name: 'analysis-susi-cards-report',
    component: () => import('#/pages/fo/analysis/SusiCardsPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '수시 6장 리포트' },
  },
  // 진단 리포트 상세(040 WS-04 조회 스택) — 본인 진단만(서버 404 격리).
  {
    path: '/diagnosis/:id',
    name: 'analysis-diagnosis-detail',
    component: () => import('#/pages/fo/analysis/DiagnosisDetailPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '진단 상세' },
  },
  // 진로심리검사(022) 목록. 077 — 비로그인은 샘플 뷰(4종 소개·결과 발췌), 회원 데이터 분기는 화면이 세션으로
  // 소유. 응시·결과 자식 라우트는 본인 데이터라 인증을 유지한다.
  {
    path: '/tests',
    name: 'analysis-tests',
    component: () => import('#/pages/fo/analysis/TestCenterPage.vue'),
    meta: { layout: 'workspace', title: '진로심리검사' },
  },
  // 응시는 몰입 모드(043) — 워크스페이스 셸 제거(blank), 인증은 유지(본인 응시 데이터).
  {
    path: '/tests/:testCode',
    name: 'analysis-test-take',
    component: () => import('#/pages/fo/analysis/TestTakePage.vue'),
    meta: { layout: 'blank', requiresAuth: true, title: '검사 진행' },
  },
  {
    path: '/results/:resultId',
    name: 'analysis-test-result',
    component: () => import('#/pages/fo/analysis/TestResultPage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '검사 결과' },
  },
  {
    path: '/reports',
    name: 'analysis-reports',
    component: () => import('#/pages/fo/analysis/ReportArchivePage.vue'),
    meta: { layout: 'workspace', requiresAuth: true, title: '리포트 보관함' },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

/**
 * 스크롤 복원/앵커 정책.
 * - 뒤로/앞으로(savedPosition): 브라우저 보존 위치로.
 * - 해시 앵커(예: 다른 페이지 → /#price): 라우터 기본 스크롤을 끄고(false), 레이아웃이
 *   정착한 뒤 scrollToHash 가 정확히 보정. 라우터의 1회성 좌표 계산은 위쪽 비동기 콘텐츠가
 *   부풀기 전에 끝나 어긋나므로 사용하지 않는다.
 * - 그 외: 상단으로.
 */
export const scrollBehavior: RouterScrollBehavior = (to, _from, savedPosition) => {
  if (savedPosition) return savedPosition;
  if (to.hash) {
    scrollToHash(to.hash);
    return false;
  }
  return { top: 0 };
};

/**
 * 인증 가드 등록 — `meta.requiresAuth` 라우트는 로그인한 사용자만 통과시킨다.
 * 차단 시 /error/auth-required 로 보내고 원래 목적지(redirect)를 쿼리에 보존한다.
 * ⚠️ 클라 가드는 UX 차단일 뿐 — 실제 접근 강제는 백엔드 @PreAuthorize(IA 03:282).
 *
 * 부팅 세션 복원(FR-017·SC-011): 메모리 authLevel 은 새로고침마다 guest 로 초기화되므로, 첫 내비게이션에서
 * 영속 쿠키로 서버 세션을 1회 확인해 로그인 상태를 되살린다. restoreSession() 은 완료 플래그로 1회만 실제
 * 호출되며, 이를 보호 라우트 판정 전에 await 해야 유효 세션이 guest 로 오인돼 로그인 화면으로 튕기는 일을 막는다.
 *
 * SSG: 빌드 타임 프리렌더는 각 라우트를 직접 렌더하며 router.push 네비게이션이 없어 가드가 돌지 않는다.
 * 그래도 가드는 클라이언트(브라우저)에서만 등록해 빌드 환경에서 useAuthStore/restoreSession(API)이
 * 실행될 여지를 원천 차단한다(main.ts 가 isClient 일 때만 호출).
 */
export function registerAuthGuard(router: Router): void {
  router.beforeEach(async (to) => {
    const auth = useAuthStore();

    // 첫 진입 시 쿠키 세션 복원(이미 복원됐으면 즉시 반환). 보호 라우트 판정의 정확성을 위해 먼저 완료한다.
    if (!auth.sessionRestored) {
      await auth.restoreSession();
    }

    if (!to.meta.requiresAuth) return true;
    if (auth.isAuthenticated) return true;

    return {
      name: 'error-auth-required',
      query: { redirect: to.fullPath },
    };
  });
}
